'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import type { FilterGroup, SelectedFilters } from '@/types/filter';
import type { Game as GameT } from '@/types/games';
import type { ServerContext } from '@/store/server-context';

import { CategoryFilter } from '@/app/(pages)/(category)/c/[slug]/components/category-filter';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { Breadcrumbs } from '@/app/(pages)/(category)/c/[slug]/components/breadcrumbs';
import { SORTING_ORDER, SortingType } from './constants';
import { CategoryFilterProvider } from '@/app/(pages)/(category)/c/[slug]/components/category-filter/context/category-filter-context';
import { getFilteredGames } from '@/app/(pages)/(category)/c/[slug]/components/category-filter/utils';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { ExplorePageWrapper } from '@/app/components/explore/explore-page-wrapper/explore-page-wrapper';
import { classes } from '@/utils/classes/classes';
import { INITIAL_SELECTED_FILTERS, ZONES } from '@/utils/constants';
import { getText } from '@/utils/text/text';
import { useStore } from '@/store/store';
import type { Zone } from '@/types/zones/zone';
// eslint-disable-next-line nextjs-boundaries/no-server-import-in-client
import { GameFactoryClient } from '@/app/components/game/game-factory.client';
import { getGameName } from '@/logic/game-adapter/game-adapter';
import { parseGamesList } from '@/logic/games/parse-games-list';
import { VirtualGamesGrid } from '@/app/components/virtual-games-grid/virtual-games-grid';
import { CategoryLayout } from '@/app/(pages)/(category)/c/[slug]/utils';
import { useGlobalContext } from '@/store/contexts/global-context';
import { PRODUCT_THROTTLES } from '@/utils/feature-flags/constants';
import { ZoneLayoutProvider } from '@/app/components/zones/contexts/zone-layout-context';
import { PRISMIC_ZONE_LAYOUT, FAVORITES_ZONE_NAME } from '@/app/constants/zone';
import { useNewCategory } from '@/app/components/zones/contexts/new-category-context';
import {
    CategoryTrackingProvider,
    useCategoryPageImpressionEvent,
    useCategoryViewEvent
} from '@/tracking/snowplow/contexts/category-tracking-context';

type Props = {
    zoneUid: string;
    zoneTitle: string;
    isFavoriteZone: boolean;
    categoryName: string;
    categoryUid: string;
    canFilter: boolean;
    allGames: GameT[];
    filtersGroups: FilterGroup[];
    serverContext: ServerContext;
    headerZone: Zone;
    categoryHeaderComponent?: ReactNode | null;
    zonesComponents?: Record<string, ReactNode> | null;
    layout: CategoryLayout;
};

export const CategoryPageClient = ({
    zoneUid,
    zoneTitle,
    categoryName,
    categoryUid,
    isFavoriteZone,
    canFilter,
    allGames,
    filtersGroups,
    serverContext,
    categoryHeaderComponent,
    zonesComponents,
    layout
}: Props) => {
    const { featureFlags, region, isMobile, isPokerDesktopClient } = useGlobalContext();
    const hasCategoryV2Layout = featureFlags[PRODUCT_THROTTLES.CATEGORY_V2_LAYOUT];
    const favoriteGames = useStore((state) => state.userContext.favoriteGames);
    const uiToggles = useStore((state) => state.uiToggles);
    const { newToYouUIDs } = useNewCategory();
    const [sortingType, setSortingType] = useState<SortingType | null>(null);
    const [appliedFilters, setAppliedFilters] = useState<SelectedFilters>(INITIAL_SELECTED_FILTERS);

    const isLoggedIn = serverContext.isLoggedIn;
    const [isVirtual, setIsVirtual] = useState<boolean>(isLoggedIn || serverContext.isAndroidWrapper);

    const categoryTrackingValue = useMemo(
        () => ({
            categoryName,
            totalGamesInCategory: allGames.length,
            zoneUid,
            categoryUid
        }),
        [categoryName, allGames.length, zoneUid, categoryUid]
    );

    useEffect(() => {
        if (isLoggedIn && !isVirtual) {
            setIsVirtual(true);
        }
    }, [isLoggedIn, isVirtual]);

    useCategoryPageImpressionEvent(categoryTrackingValue, allGames);
    useCategoryViewEvent(categoryTrackingValue);

    useEffect(() => {
        return () => {
            if (layout === CategoryLayout.NEW) {
                uiToggles.setShowNewGames(false);
            }
        };
    }, []);

    const baseGames = useMemo(
        () => (isFavoriteZone ? allGames.filter((game) => favoriteGames.includes(game.uid)) : allGames),
        [allGames, favoriteGames, isFavoriteZone]
    );

    const hasActiveFilters = useMemo(
        () => Object.values(appliedFilters).some((filters) => filters.length > 0),
        [appliedFilters]
    );

    const sortGames = (direction: number, list: GameT[]) =>
        [...list].sort((a, b) => {
            const nameA = getGameName(a.data?.display_name)?.trim?.().toLowerCase?.() ?? '';
            const nameB = getGameName(b.data?.display_name)?.trim?.().toLowerCase?.() ?? '';
            return direction > 0 ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
        });

    const filteredGames = useMemo(() => getFilteredGames(baseGames, appliedFilters), [baseGames, appliedFilters]);

    const sortedGames = useMemo(() => {
        if (sortingType === null) return filteredGames;

        const direction = sortingType === SORTING_ORDER.AZ ? 1 : -1;
        return sortGames(direction, filteredGames);
    }, [filteredGames, sortingType]);

    const exploreGames = useMemo(
        () =>
            layout === CategoryLayout.NEW && newToYouUIDs.size
                ? sortedGames.filter((game) => !newToYouUIDs.has(game.uid))
                : sortedGames,
        [layout, sortedGames, newToYouUIDs]
    );

    const adaptedGames = useMemo(
        () => parseGamesList(sortedGames, PRISMIC_ZONE_LAYOUT.DEFAULT, { region, isMobile }).games,
        [sortedGames, region, isMobile]
    );

    useEffect(() => {
        if ((hasActiveFilters || isLoggedIn || serverContext.isAndroidWrapper) && !isVirtual) {
            setIsVirtual(true);
        }
    }, [hasActiveFilters, isLoggedIn, serverContext.isAndroidWrapper, isVirtual]);

    const handleSorting = (type: SortingType | null) => {
        setSortingType(type);

        if (!isVirtual) {
            setIsVirtual(true);
        }
    };

    if (layout === CategoryLayout.NEW) {
        const newToYouZone = zonesComponents?.[ZONES.NEW_TO_YOU];

        return (
            <CategoryTrackingProvider value={categoryTrackingValue}>
                <div className="explore-content-container">
                    {newToYouZone && (
                        <div className="single-zone-component" data-zoneuid={ZONES.NEW_TO_YOU}>
                            <div className="gapped">{newToYouZone}</div>
                        </div>
                    )}
                    <div className="single-zone-component" data-zoneuid={zoneUid}>
                        <ExplorePageWrapper
                            title={zoneTitle}
                            games={exploreGames}
                            isMobile={isMobile}
                            region={region}
                        />
                    </div>
                </div>
            </CategoryTrackingProvider>
        );
    }

    const shouldShowZones =
        !isFavoriteZone && hasCategoryV2Layout && sortingType === null && !hasActiveFilters && zonesComponents;
    // The breadcrumbs hold the category page H1 top title, which must render on mobile web for SEO
    // Only the poker desktop client is excluded, since its wrapper renders own chrome.
    const shouldShowBreadcrumbs = !isPokerDesktopClient;
    const trackingZoneName = isFavoriteZone ? FAVORITES_ZONE_NAME : zoneTitle;

    return (
        <CategoryTrackingProvider value={categoryTrackingValue}>
            {categoryHeaderComponent}
            <div className="gapped" data-t-module={categoryName}>
                <div className="filters-container">
                    {shouldShowBreadcrumbs && (
                        <Breadcrumbs linkLabel={categoryName} hasCategoryV2Layout={hasCategoryV2Layout} />
                    )}

                    <CategoryFilterProvider
                        filtersGroups={filtersGroups}
                        appliedFilters={appliedFilters}
                        setAppliedFiltersAction={setAppliedFilters}
                        categoryName={categoryName}
                        canFilter={canFilter}
                        games={baseGames}>
                        <CategoryFilter sortingType={sortingType} onSortClick={handleSorting} />
                    </CategoryFilterProvider>
                </div>

                {shouldShowZones ? (
                    <div className="category-zones">
                        {Object.entries(zonesComponents).map(([uid, component]) => (
                            <div key={uid} className="zone">
                                {component}
                            </div>
                        ))}
                    </div>
                ) : (
                    <ZoneLayoutProvider
                        layout={hasCategoryV2Layout ? PRISMIC_ZONE_LAYOUT.ONE_ROW : PRISMIC_ZONE_LAYOUT.DEFAULT}>
                        <div
                            className={classes(
                                'single-zone-component',
                                !hasCategoryV2Layout && 'gapped',
                                hasCategoryV2Layout && 'v2'
                            )}
                            data-zoneuid={zoneUid}
                            data-t-zoneuid={zoneUid}
                            data-t-zonename={trackingZoneName}
                            data-t-zonelayout="game_list">
                            {!hasCategoryV2Layout && !isPokerDesktopClient && (
                                <h2 className="zone-title-text">{zoneTitle}</h2>
                            )}

                            {isVirtual ? (
                                <VirtualGamesGrid games={adaptedGames} />
                            ) : (
                                <div className="grid">
                                    {adaptedGames.map((game, index) => (
                                        <GameFactoryClient
                                            key={game.uid}
                                            game={game}
                                            layout={PRISMIC_ZONE_LAYOUT.DEFAULT}
                                            priority={index === 0}
                                            tracking={{ gamePosition: index }}
                                        />
                                    ))}
                                </div>
                            )}

                            {isFavoriteZone && sortedGames.length === 0 && (
                                <div className="no-favorite-games">
                                    {getText({ text: 'components.noFavouriteGamesMessage' })}
                                </div>
                            )}
                        </div>
                    </ZoneLayoutProvider>
                )}
            </div>
        </CategoryTrackingProvider>
    );
};
